import { Box, HStack, Icon, Link, Text } from '@chakra-ui/react'
import { ServiceStatus } from '@infraloop/services'
import { IconType } from 'react-icons'
import { LuArrowUpRight, LuInfo, LuTriangleAlert } from 'react-icons/lu'

type NotificationType = NonNullable<ServiceStatus['notification']>['type']

interface StripStyle {
  bg: string
  color: string
  borderColor: string
  icon: IconType
  iconColor: string
  link: { bg: string; color: string; borderColor: string }
}

/** Per-type styling for the notification strip, derived from the mockup. */
const STRIP_STYLES: Record<NotificationType, StripStyle> = {
  info: {
    bg: 'primary.50',
    color: 'primary.800',
    borderColor: 'primary.100',
    icon: LuInfo,
    iconColor: 'primary.600',
    link: { bg: 'white', color: 'primary.700', borderColor: 'primary.200' },
  },
  warning: {
    bg: 'orange.100',
    color: 'orange.700',
    borderColor: 'orange.200',
    icon: LuTriangleAlert,
    iconColor: 'orange.600',
    link: { bg: 'whiteAlpha.700', color: 'orange.800', borderColor: 'orange.300' },
  },
  error: {
    bg: 'red.600',
    color: 'white',
    borderColor: 'red.700',
    icon: LuTriangleAlert,
    iconColor: 'white',
    link: { bg: 'blackAlpha.300', color: 'white', borderColor: 'whiteAlpha.400' },
  },
}

const NotificationStrip = ({
  notification,
}: {
  /** An enabled service-status notification to display. */
  notification: NonNullable<ServiceStatus['notification']>
}) => {
  const style = STRIP_STYLES[notification.type]
  const hasLink = Boolean(notification.url && notification.urlText)

  return (
    <Box
      w="100%"
      bg={style.bg}
      color={style.color}
      borderBottom="1px solid"
      borderColor={style.borderColor}
      py="10px"
      px="24px"
      data-testid={`notification-${notification.type}`}
    >
      <HStack justify="center" gap="12px" fontSize="13px" fontWeight="medium" flexWrap="wrap">
        <HStack gap="8px">
          <Icon as={style.icon} boxSize="15px" color={style.iconColor} />
          <Text as="span">{notification.message}</Text>
        </HStack>
        {hasLink && (
          <Link
            href={notification.url}
            display="inline-flex"
            alignItems="center"
            gap="6px"
            px="11px"
            py="4px"
            borderRadius="6px"
            border="1px solid"
            borderColor={style.link.borderColor}
            bg={style.link.bg}
            color={style.link.color}
            fontSize="12px"
            fontWeight="semibold"
            textDecoration="none"
            _hover={{ textDecoration: 'none' }}
          >
            {notification.urlText}
            <Icon as={LuArrowUpRight} boxSize="13px" />
          </Link>
        )}
      </HStack>
    </Box>
  )
}

export default NotificationStrip
